import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from 'react-i18next';

// Design System
import Input from "../../components/Input/Input";
import { Button } from "../../components/Button/Button";
import Modal from "../../components/Modal/Modal";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";

interface CompanysDialogProps {
    visible: boolean;
    onHide: () => void;
    onCreate: (name: string, description: string) => void;
    companys: { id: string; name: string; description: string } | null;
}

const CompanysDialog: React.FC<CompanysDialogProps> = ({ visible, onHide, onCreate, companys }) => {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [errors, setErrors] = useState<{ name?: string; description?: string }>({});
    const [showConfirmCancel, setShowConfirmCancel] = useState(false);

    const { t: translateMessage } = useTranslation("messages");
    const { t: translateForm } = useTranslation("form");
    const { t: translateGeneral } = useTranslation("general");

    const isDirty = useMemo(() => {
        if (companys) {
            return name !== companys.name || description !== companys.description;
        }
        return name !== "" || description !== "";
    }, [name, description, companys]);

    // Preenche os campos se a prop companys estiver disponível
    useEffect(() => {
        if (visible) {
            if (companys) {
                setName(companys.name);
                setDescription(companys.description);
            } else {
                setName("");
                setDescription("");
            }
            setErrors({});
        }
    }, [visible, companys]);

    const handleCreate = (): void => {
        let valid = true;
        const newErrors: { name?: string; description?: string } = {};

        if (!name.trim()) {
            newErrors.name = translateMessage("namerequired");
            valid = false;
        } else if (name.length > 300) {
            newErrors.name = translateMessage("nameQtdCharacters");
            valid = false;
        }
        if (!description.trim()) {
            newErrors.description = translateMessage("descriptionRequired")
            valid = false;
        } else if (description.length > 1000) {
            newErrors.description = translateMessage("descriptionQtdCharacters")
            valid = false;
        }

        if (!valid) {
            setErrors(newErrors);
            return;
        }

        onCreate(name, description);
        onHide();
    };

    const handleCancel = (): void => {
        if (isDirty) {
            setShowConfirmCancel(true);
        } else {
            onHide();
        }
    };

    return (
        <>
            <Modal
                header={companys ? translateGeneral("editCompany") : translateGeneral("newCompany")}
                visible={visible}
                onHide={handleCancel}
                size="md"
                footer={
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                        <Button
                            onClick={handleCancel}
                            label={translateGeneral("cancel")}
                            severity="secondary"
                            variant="text"
                        />
                        <Button
                            onClick={handleCreate}
                            label={companys ? translateGeneral("update") : translateGeneral("register")}
                            icon="save"
                            severity="primary"
                        />
                    </div>
                }
            >
                <div style={{ display: "flex", flexDirection: "column", gap: "1rem", paddingTop: "0.5rem" }}>
                    <Input
                        type="text"
                        label={translateForm("name")}
                        value={name}
                        onChange={(val: any) => {
                            setName(val);
                            setErrors((prev) => ({ ...prev, name: undefined }));
                        }}
                        required
                        className={errors.name ? "p-invalid" : ""}
                    />
                    {errors.name && <small className="p-error" style={{ color: "var(--color-error-500)" }}>{errors.name}</small>}

                    <Input
                        type="text"
                        label={translateForm("description")}
                        value={description}
                        onChange={(val: any) => {
                            setDescription(val);
                            setErrors((prev) => ({ ...prev, description: undefined }));
                        }}
                        required
                        className={errors.description ? "p-invalid" : ""}
                    />
                    {errors.description && <small className="p-error" style={{ color: "var(--color-error-500)" }}>{errors.description}</small>}
                </div>
            </Modal>

            <ConfirmModal
                visible={showConfirmCancel}
                onHide={() => setShowConfirmCancel(false)}
                onConfirm={() => {
                    setShowConfirmCancel(false);
                    onHide();
                }}
            />
        </>
    );
};

export default CompanysDialog;
